import { Info } from '@mui/icons-material';
import {
    Alert,
    Box,
    FormControlLabel,
    Switch,
    Tooltip,
    Typography,
    styled,
} from '@mui/material';
import { ReactNode } from 'react';
import { DISTRIBUTION_HELP_TEXT, DistributionWidgetContainer } from 'src/components/SetsPlayground/Widgets/DistributionWidgetContainer';
import { Collapsable } from 'src/components/common/Collapsable/Collapsable';
import { CollapsableToggle } from 'src/components/common/Collapsable/CollapsableToggle';

type SetDefinition = Models.SetsPlayground.SetDefinition;
type SetDistribution = Models.SetsPlayground.SetDistribution;

const Card = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.divider}`,
    borderLeft: `${theme.spacing(1)} solid ${theme.palette.grey[500]}`,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(4),
    marginBottom: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
}));

const HeaderRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(3),
    alignItems: 'center',
    justifyContent: 'space-between',
}));

const HeaderActions = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

const Details = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    paddingTop: theme.spacing(3),
}));

const DistributionSection = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
}));

interface SyntheticSetCardProps {
    title: string;
    tooltip: string;
    subtitle: string;
    enabled: boolean;
    expanded: boolean;
    distribution: SetDistribution;
    validationErrors: string[];
    headerContent?: ReactNode;
    onToggleEnabled: (enabled: boolean) => void;
    onToggleExpanded: (expanded: boolean) => void;
    onChangeDistribution: (distribution: SetDistribution) => void;
}

export const SyntheticSetCard = ({
    title,
    tooltip,
    subtitle,
    enabled,
    expanded,
    distribution,
    validationErrors,
    headerContent,
    onToggleEnabled,
    onToggleExpanded,
    onChangeDistribution,
}: SyntheticSetCardProps): JSX.Element => (
    <Card>
        <HeaderRow>
            <Box>
                <Typography
                    sx={{ display: 'flex', alignItems: 'center', fontWeight: 'fontWeightMedium' }}
                    variant="body1"
                >
                    {title}
                    <Tooltip title={tooltip}>
                        <Info sx={{
                            ml: 1, height: 16, width: 16, color: 'primary.main',
                        }}
                        />
                    </Tooltip>
                </Typography>
                <Typography color="text.secondary" variant="body2">
                    {subtitle}
                </Typography>
            </Box>
            {headerContent}
            <HeaderActions>
                <FormControlLabel
                    control={(
                        <Switch
                            checked={enabled}
                            onChange={(e): void => onToggleEnabled(e.target.checked)}
                        />
                    )}
                    label={enabled ? 'On' : 'Off'}
                    labelPlacement="start"
                />
                <CollapsableToggle
                    aria-label={`${expanded ? 'Collapse' : 'Expand'} ${title.toLowerCase()} details`}
                    expanded={expanded}
                    size="small"
                    onClick={(_event, isExpanded): void => onToggleExpanded(isExpanded)}
                />
            </HeaderActions>
        </HeaderRow>
        <Collapsable expanded={expanded}>
            <Details>
                <DistributionSection>
                    <Typography
                        sx={{ display: 'flex', alignItems: 'center', fontWeight: 'fontWeightMedium' }}
                        variant="body1"
                    >
                        Distribution
                        <Tooltip title={DISTRIBUTION_HELP_TEXT}>
                            <Info sx={{
                                ml: 1, height: 16, width: 16, color: 'primary.main',
                            }}
                            />
                        </Tooltip>
                    </Typography>
                    <DistributionWidgetContainer
                        disabled={!enabled}
                        index={0}
                        set={{ distribution } as SetDefinition}
                        onChangeDistribution={(_index, next): void => onChangeDistribution(next)}
                    />
                    {validationErrors.length > 0 && (
                        <Alert severity="error">
                            {validationErrors.map((message) => (
                                <div key={message}>{message}</div>
                            ))}
                        </Alert>
                    )}
                </DistributionSection>
            </Details>
        </Collapsable>
    </Card>
);
